import { useEffect, useRef, useLayoutEffect } from "react"
import { useMotionValue } from "framer-motion"
import styled from "@emotion/styled"

import { media } from "css/breakpoints"
import { useViewport } from "contexts/Viewport"
import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import useConstant from "hooks/useConstant"

const Can = styled.canvas`
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`

export default function Sequence({ image, anim_range_start, anim_range_end, prog }) {
  const console = useConsole()
  const viewport = useViewport()

  anim_range_start = anim_range_start > anim_range_end ? -anim_range_start : anim_range_start

  const ref = useRef()
  const canCtx = useRef()
  const frameNb = useConstant(() => Math.abs(anim_range_end - anim_range_start))
  const frames = useConstant(
    () =>
      new Map([
        ["portrait", new Map()],
        ["landscape", new Map()],
      ])
  )
  const sources = useConstant(
    () =>
      new Map([
        ["portrait", image.sources.find(o => o.media === media.portrait)],
        ["landscape", image.sources.find(o => o.media === media.landscape)],
      ])
  )

  const currentFrame = useMotionValue(-1)

  function getTemplate() {
    return viewport.isMobile.get() ? sources.get("portrait").src.replace(/[0-9]{4}/gi, "%") : sources.get("landscape").src.replace(/[0-9]{4}/gi, "%")
  }

  const preloadImages = () => {
    for (let i = anim_range_start; i <= anim_range_end; i++) {
      const img = new Image()
      img.src = getTemplate().replace("%", `${Math.abs(i).toString().padStart(4, "0")}`)
    }
  }

  async function feedImgArray() {
    if (viewport.isMobile.get() && !!frames.get("portrait").size) {
      return Promise.resolve()
    } else if (!viewport.isMobile.get() && !!frames.get("landscape").size) {
      return Promise.resolve()
    }

    const template = getTemplate()
    const promArr = []
    const orient = viewport.isMobile.get() ? "portrait" : "landscape"

    for (let i = anim_range_start; i <= anim_range_end; i++) {
      promArr.push(
        new Promise((resolve, reject) => {
          const img = new Image()
          img.src = template.replace("%", `${Math.abs(i).toString().padStart(4, "0")}`)
          img.onload = () => resolve(img)
          img.onerror = err => reject(err)
        })
      )
    }

    return Promise.all(promArr).then(arr => frames.set(orient, new Map(arr.map((o, i) => [i, o]))))
  }

  const updateImage = () => {
    const o = viewport.isMobile.get() ? "portrait" : "landscape"
    if (!frames.get(o).size || !frames.get(o).get(currentFrame.get())) return
    canCtx.current.clearRect(0, 0, sources.get(o).width, sources.get(o).height)
    canCtx.current.drawImage(frames.get(o).get(currentFrame.get()), 0, 0, sources.get(o).width, sources.get(o).height)
  }
  function onProg(p) {
    currentFrame.set(Math.min(frameNb, Math.ceil(p * frameNb)))
  }
  useEffect(() => prog.onChange(onProg))

  function onFrame(f) {
    updateImage()
  }
  useEffect(() => currentFrame.onChange(onFrame))

  function onOrChange(bool) {
    const o = bool ? "portrait" : "landscape"
    updateSize(bool)
    if (frames.get(o).size) return
    preloadImages()
    feedImgArray().then(() => updateImage(currentFrame.get()))
  }
  useEffect(() => viewport.isMobile.onChange(onOrChange))

  function updateSize(bool) {
    ref.current.width = bool ? image.sources[0].width : image.sources[1].width
    ref.current.height = bool ? image.sources[0].height : image.sources[1].height
  }

  useLayoutEffect(() => {
    preloadImages()
    canCtx.current = ref.current.getContext("2d")
    updateSize(viewport.isMobile.get())
    feedImgArray().then(() => updateImage(0))
  }, [])

  return <Can ref={ref} />
}
